import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { 
  Monitor, 
  Bell, 
  Calendar, 
  Users, 
  Clock, 
  Palette,
  Type,
  Eye,
  EyeOff,
  Save,
  RefreshCw
} from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";

interface HeaderConfig {
  showDate: boolean;
  dateFormat: string;
  showClientStatuses: boolean;
  showNotifications: boolean;
  showSystemHealth: boolean;
  notificationBar: {
    enabled: boolean;
    message: string;
    backgroundColor: string;
    textColor: string;
    position: 'top' | 'bottom';
    dismissible: boolean;
  };
}

export function HeaderSettings() {
  const { toast } = useToast();
  const [config, setConfig] = useState<HeaderConfig>({
    showDate: true,
    dateFormat: 'dd/MM/yyyy',
    showClientStatuses: true,
    showNotifications: true,
    showSystemHealth: true,
    notificationBar: {
      enabled: false,
      message: 'مرحباً بكم في نظام إدارة الشركات السياحية',
      backgroundColor: '#3b82f6',
      textColor: '#ffffff',
      position: 'top',
      dismissible: true,
    }
  });

  const [previewMode, setPreviewMode] = useState(false);

  const handleConfigChange = (key: keyof HeaderConfig, value: any) => {
    setConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleNotificationBarChange = (key: keyof HeaderConfig['notificationBar'], value: any) => {
    setConfig(prev => ({
      ...prev,
      notificationBar: {
        ...prev.notificationBar,
        [key]: value
      }
    }));
  };

  const handleSave = () => {
    // Save configuration to localStorage or API
    localStorage.setItem('headerConfig', JSON.stringify(config));
    toast({
      title: "تم الحفظ بنجاح",
      description: "تم حفظ إعدادات الشريط العلوي",
    });
  };

  const handleReset = () => {
    setConfig({
      showDate: true,
      dateFormat: 'dd/MM/yyyy',
      showClientStatuses: true,
      showNotifications: true,
      showSystemHealth: true,
      notificationBar: {
        enabled: false,
        message: 'مرحباً بكم في نظام إدارة الشركات السياحية',
        backgroundColor: '#3b82f6',
        textColor: '#ffffff',
        position: 'top',
        dismissible: true,
      }
    });
    toast({
      title: "تم إعادة التعيين",
      description: "تم إعادة تعيين الإعدادات للقيم الافتراضية",
    });
  };

  return (
    <div className="space-y-6">
      {/* Preview Mode Toggle */}
      <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10 hover:shadow-2xl transition-all duration-300">
        <CardHeader className="border-b border-gradient-to-r from-transparent via-info/20 to-transparent">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-info to-info/80 shadow-lg shadow-info/30">
              <Monitor className="h-5 w-5 text-white" />
            </div>
            <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">معاينة التغييرات</span>
          </CardTitle>
          <CardDescription className="text-base">
            قم بتفعيل وضع المعاينة لرؤية التغييرات مباشرة
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="flex items-center justify-between p-4 bg-gradient-to-br from-muted/20 to-muted/10 rounded-xl border border-border/50">
            <Label className="text-base font-medium">وضع المعاينة المباشرة</Label>
            <div className="flex items-center gap-3">
              <Switch
                checked={previewMode}
                onCheckedChange={setPreviewMode}
              />
              {previewMode ? <Eye className="h-5 w-5 text-success" /> : <EyeOff className="h-5 w-5 text-muted-foreground" />}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Header Content Settings */}
      <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10 hover:shadow-2xl transition-all duration-300">
        <CardHeader className="border-b border-gradient-to-r from-transparent via-[hsl(var(--tab-header))]/20 to-transparent">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-[hsl(var(--tab-header))] to-[hsl(var(--tab-header))]/80 shadow-lg shadow-[hsl(var(--tab-header))]/30">
              <Monitor className="h-5 w-5 text-white" />
            </div>
            <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">محتوى الشريط العلوي</span>
          </CardTitle>
          <CardDescription className="text-base">
            تحكم في العناصر التي تظهر في الشريط العلوي
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 p-6">
          {/* Date Settings */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <Label>عرض التاريخ</Label>
              </div>
              <Switch
                checked={config.showDate}
                onCheckedChange={(checked) => handleConfigChange('showDate', checked)}
              />
            </div>
            
            {config.showDate && (
              <div className="ml-6 space-y-2">
                <Label>تنسيق التاريخ</Label>
                <Select 
                  value={config.dateFormat} 
                  onValueChange={(value) => handleConfigChange('dateFormat', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dd/MM/yyyy">يوم/شهر/سنة (31/12/2024)</SelectItem>
                    <SelectItem value="MM/dd/yyyy">شهر/يوم/سنة (12/31/2024)</SelectItem>
                    <SelectItem value="yyyy-MM-dd">سنة-شهر-يوم (2024-12-31)</SelectItem>
                    <SelectItem value="dd MMMM yyyy">يوم شهر سنة (31 ديسمبر 2024)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <Separator />

          {/* Client Status Settings */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <Label>عرض حالات العملاء</Label>
            </div>
            <Switch
              checked={config.showClientStatuses}
              onCheckedChange={(checked) => handleConfigChange('showClientStatuses', checked)}
            />
          </div>

          <Separator />

          {/* Notifications Settings */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              <Label>عرض الإشعارات</Label>
            </div>
            <Switch
              checked={config.showNotifications}
              onCheckedChange={(checked) => handleConfigChange('showNotifications', checked)}
            />
          </div>

          <Separator />

          {/* System Health Settings */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Monitor className="h-4 w-4" />
              <Label>عرض حالة النظام</Label>
            </div>
            <Switch
              checked={config.showSystemHealth}
              onCheckedChange={(checked) => handleConfigChange('showSystemHealth', checked)}
            />
          </div>
        </CardContent>
      </Card>

      {/* Notification Bar Settings */}
      <Card className="border-border/50 shadow-xl bg-gradient-to-br from-background via-background to-muted/10 hover:shadow-2xl transition-all duration-300">
        <CardHeader className="border-b border-gradient-to-r from-transparent via-[hsl(var(--tab-notifications))]/20 to-transparent">
          <CardTitle className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-[hsl(var(--tab-notifications))] to-[hsl(var(--tab-notifications))]/80 shadow-lg shadow-[hsl(var(--tab-notifications))]/30">
              <Bell className="h-5 w-5 text-white" />
            </div>
            <span className="bg-gradient-to-r from-foreground to-muted-foreground bg-clip-text text-transparent">شريط الإشعارات العلوي</span>
          </CardTitle>
          <CardDescription className="text-base">
            إعداد شريط الإشعارات الذي يظهر أعلى الموقع
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 p-6">
          <div className="flex items-center justify-between">
            <Label>تفعيل شريط الإشعارات</Label>
            <Switch
              checked={config.notificationBar.enabled}
              onCheckedChange={(checked) => handleNotificationBarChange('enabled', checked)}
            />
          </div>

          {config.notificationBar.enabled && (
            <div className="space-y-4 pl-4 border-l-2 border-primary/20">
              {/* Message Content */}
              <div className="space-y-2">
                <Label>نص الإشعار</Label>
                <Textarea
                  value={config.notificationBar.message}
                  onChange={(e) => handleNotificationBarChange('message', e.target.value)}
                  placeholder="اكتب نص الإشعار هنا..."
                  className="min-h-[80px]"
                />
              </div>

              {/* Position */}
              <div className="space-y-2">
                <Label>موضع الشريط</Label>
                <Select 
                  value={config.notificationBar.position} 
                  onValueChange={(value) => handleNotificationBarChange('position', value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="top">أعلى الصفحة</SelectItem>
                    <SelectItem value="bottom">أسفل الصفحة</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Colors */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>لون الخلفية</Label>
                  <Input
                    type="color"
                    value={config.notificationBar.backgroundColor}
                    onChange={(e) => handleNotificationBarChange('backgroundColor', e.target.value)}
                    className="w-full h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label>لون النص</Label>
                  <Input
                    type="color"
                    value={config.notificationBar.textColor}
                    onChange={(e) => handleNotificationBarChange('textColor', e.target.value)}
                    className="w-full h-10"
                  />
                </div>
              </div>

              {/* Dismissible */}
              <div className="flex items-center justify-between">
                <Label>قابل للإغلاق</Label>
                <Switch
                  checked={config.notificationBar.dismissible}
                  onCheckedChange={(checked) => handleNotificationBarChange('dismissible', checked)}
                />
              </div>

              {/* Preview */}
              <div className="space-y-2">
                <Label>معاينة الشريط</Label>
                <div 
                  className="p-3 rounded-md text-sm font-medium flex items-center justify-between"
                  style={{
                    backgroundColor: config.notificationBar.backgroundColor,
                    color: config.notificationBar.textColor
                  }}
                >
                  <span>{config.notificationBar.message}</span>
                  {config.notificationBar.dismissible && (
                    <Button size="sm" variant="ghost" className="h-6 w-6 p-0">
                      ×
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-3">
            <Button onClick={handleSave} className="flex items-center gap-2">
              <Save className="h-4 w-4" />
              حفظ التغييرات
            </Button>
            <Button variant="outline" onClick={handleReset} className="flex items-center gap-2">
              <RefreshCw className="h-4 w-4" />
              إعادة تعيين
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Current Configuration Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="h-5 w-5" />
            ملخص الإعدادات الحالية
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {config.showDate && <Badge variant="secondary">التاريخ مفعل</Badge>}
            {config.showClientStatuses && <Badge variant="secondary">حالات العملاء مفعل</Badge>}
            {config.showNotifications && <Badge variant="secondary">الإشعارات مفعل</Badge>}
            {config.showSystemHealth && <Badge variant="secondary">حالة النظام مفعل</Badge>}
            {config.notificationBar.enabled && <Badge variant="default">شريط الإشعارات مفعل</Badge>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}